import { useMemo, useRef, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Message from '../Message';
import Loader from '../Loader';
import {
  useAdminAssignBarcodeMutation,
  useLazyAdminBarcodeLookupQuery,
} from '../../slices/productsApiSlice';
import { getProductDisplayName } from '../../utils/productTitle';
import { formatMaterial } from '../../utils/material';

const rowLabel = (product) => product?.title || product?.displayName || getProductDisplayName(product);
const getTotalShelfQty = (product) =>
  (Array.isArray(product?.allInventoryLocations) ? product.allInventoryLocations : []).reduce(
    (sum, loc) => sum + (Number(loc?.quantity) || 0),
    0
  );

const AdminBarcodeLookup = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [resultProduct, setResultProduct] = useState(null);
  const [multipleMatches, setMultipleMatches] = useState([]);
  const [showChooser, setShowChooser] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState('');
  const [assignLookupKey, setAssignLookupKey] = useState('');
  const [assignLocation, setAssignLocation] = useState('');
  const [triggerLookup, { isFetching }] = useLazyAdminBarcodeLookupQuery();
  const [assignBarcode, { isLoading: isAssigning }] = useAdminAssignBarcodeMutation();
  const scanInputRef = useRef(null);

  const hasMultiple = useMemo(() => multipleMatches.length > 0, [multipleMatches]);

  const handleSelectProduct = (product) => {
    if (!product?._id) return;
    setResultProduct(product);
    setShowChooser(false);
    setCode('');
    scanInputRef.current?.focus?.();
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    const scanned = code.trim();
    if (!scanned) {
      toast.error('Enter or scan a barcode/SKU/part number.');
      return;
    }

    setResultProduct(null);
    setMultipleMatches([]);
    setPendingBarcode('');

    try {
      const payload = await triggerLookup(scanned).unwrap();
      if (payload.mode === 'single' && payload.product) {
        handleSelectProduct(payload.product);
        return;
      }

      if (payload.mode === 'multiple' && Array.isArray(payload.products) && payload.products.length) {
        setMultipleMatches(payload.products);
        setShowChooser(true);
        return;
      }

      toast.error('Unexpected lookup response.');
    } catch (err) {
      toast.error(err?.data?.message || 'No product found for this code.');
      setPendingBarcode(scanned);
      setAssignLookupKey('');
      scanInputRef.current?.focus?.();
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!pendingBarcode) return;
    if (!assignLookupKey.trim()) {
      toast.error('Enter SKU or part number to assign this barcode.');
      return;
    }

    try {
      const body = {
        barcode: pendingBarcode,
        lookupKey: assignLookupKey.trim(),
      };
      if (assignLocation.trim()) {
        body.location = assignLocation.trim();
      }
      const payload = await assignBarcode({
        ...body,
      }).unwrap();
      setResultProduct(payload.product);
      setPendingBarcode('');
      setAssignLookupKey('');
      toast.success('Barcode assigned successfully.');
    } catch (err) {
      toast.error(err?.data?.message || 'Unable to assign barcode.');
    }
  };

  return (
    <div className='p-3 border rounded mb-3 bg-light'>
      <h5 className='mb-2'>Barcode / SKU Lookup</h5>
      <Form onSubmit={handleLookup} className='d-flex gap-2'>
        <Form.Control
          ref={scanInputRef}
          type='text'
          placeholder='Scan barcode or type SKU / part number'
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoFocus
        />
        <Button type='submit' variant='primary'>Find</Button>
      </Form>

      {isFetching && <Loader />}
      {isAssigning && <Loader />}

      {resultProduct && (
        <Message variant='success'>
          Found: <strong>{rowLabel(resultProduct)}</strong> | SKU: {resultProduct.sku || '-'} | Barcode: {resultProduct.barcode || '-'} | Location: {resultProduct.location || 'Not set'}
          {resultProduct.matchedInventoryLocation && (
            <div className='mt-2'>
              <strong>Matched scanned barcode location:</strong>{' '}
              {[
                resultProduct.matchedInventoryLocation.room,
                resultProduct.matchedInventoryLocation.section,
                resultProduct.matchedInventoryLocation.shelf,
                resultProduct.matchedInventoryLocation.column,
                resultProduct.matchedInventoryLocation.bin,
              ]
                .filter(Boolean)
                .join(' / ') || 'Location details not provided'}{' '}
              (Shelf Qty: {Number(resultProduct.matchedInventoryLocation.quantity ?? 0)})
            </div>
          )}
          {Array.isArray(resultProduct.allInventoryLocations) && resultProduct.allInventoryLocations.length > 0 && (
            <div className='mt-2 small'>
              <strong>All inventory locations ({resultProduct.allInventoryLocations.length}):</strong>
              <ul className='mb-0'>
                {resultProduct.allInventoryLocations.map((loc, idx) => (
                  <li key={`inv-${idx}`}>
                    {(loc.barcode || 'No barcode')} — {[loc.room, loc.section, loc.shelf, loc.column, loc.bin].filter(Boolean).join(' / ') || 'No location'} (Shelf Qty: {Number(loc.quantity ?? 0)})
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className='mt-2 small'>
            <div><strong>Total Shelf Qty:</strong> {getTotalShelfQty(resultProduct)}</div>
            <div><strong>Inventory Qty:</strong> {Number(resultProduct.inventoryQty ?? 0)}</div>
            <div><strong>Stock Qty:</strong> {Number(resultProduct.stockQty ?? 0)}</div>
            <div><strong>Expected Physical Qty:</strong> {Number(resultProduct.inventoryQty ?? 0) + Number(resultProduct.stockQty ?? 0)}</div>
          </div>
          <div className='mt-2'>
            <Button
              size='sm'
              variant='outline-primary'
              onClick={() => navigate(`/admin/product/${resultProduct._id}/edit`)}
            >
              Open product edit
            </Button>
          </div>
        </Message>
      )}

      {pendingBarcode && (
        <div className='border rounded p-2 mt-2 bg-white'>
          <div className='fw-semibold mb-1'>New barcode not found: {pendingBarcode}</div>
          <div className='small text-muted mb-2'>
            Assign this barcode to an existing product by SKU or part number.
          </div>
          <Form onSubmit={handleAssign}>
            <div className='d-flex flex-column flex-md-row gap-2'>
              <Form.Control
                type='text'
                placeholder='Target SKU or part number'
                value={assignLookupKey}
                onChange={(e) => setAssignLookupKey(e.target.value)}
              />
              <Form.Control
                type='text'
                placeholder='Optional location (e.g. WH-S03-C04)'
                value={assignLocation}
                onChange={(e) => setAssignLocation(e.target.value)}
              />
              <Button type='submit' variant='success'>Assign</Button>
            </div>
          </Form>
        </div>
      )}

      <Modal
        show={showChooser}
        onHide={() => {
          setShowChooser(false);
          scanInputRef.current?.focus?.();
        }}
        size='lg'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select matching product ({multipleMatches.length})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {hasMultiple ? (
            <Table striped bordered hover responsive className='table-sm align-middle'>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Barcode</th>
                  <th>Location</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Material</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {multipleMatches.map((product) => (
                  <tr key={product._id}>
                    <td>{rowLabel(product)}</td>
                    <td>{product.sku || '-'}</td>
                    <td>{product.barcode || '-'}</td>
                    <td>{product.location || 'Not set'}</td>
                    <td>{product.brand || '-'}</td>
                    <td>{product.category || '-'}</td>
                    <td>{formatMaterial(product.material) || '-'}</td>
                    <td className='text-end'>
                      <Button size='sm' onClick={() => handleSelectProduct(product)}>Select</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Message variant='info'>No products available to select.</Message>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminBarcodeLookup;
